import { APP_BASE_HREF } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { animate, AnimationFactory, state, style, transition, trigger } from '@angular/animations';

import { BehaviorSubject, filter, Subscription } from 'rxjs';

import { LinkedinSubTypeEnum } from '@Common/Basic/enums/linkedin-sub-type.enum';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { TwitterSubTypeEnum } from '@Common/Basic/enums/twitter-sub-type.enum';
import { BlueskySubTypeEnum } from '@Common/Basic/enums/bluesky-sub-type.enum';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { socialNetworkTypeColorMap } from '../../../../Core/Users/data/social-network-type-color-map';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';

import { Mention } from '../../../Mentions/models/mention';

import { webVideoHelper } from '../../../Misc/helpers/webVideo/webVideo.helper';
import { TwitterAvatarSize } from '../../../Misc/pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { socialThumbnailDefaultImage } from '../../../Misc/enums/social-thumbnail-default-image';
import { getTextAlign } from '../../../Misc/helpers/formatting/text.helper';

import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { ActionKeys } from '../../../Forms/enums/keys.enum';

import { PostViewerDialogComponent } from '../../../Dialogs/components/PostViewerDialog/post-viewer-dialog.component';



@Component({
    selector: 'agpr-post-feed-card',
    templateUrl: './post-feed-card.component.html',
    styleUrls: ['./post-feed-card.component.scss'],
    standalone: false
})
export class PostFeedCardComponent implements OnInit {

    @Input() id: string;
    @Input() mention: Mention;
    @Input() preview: boolean = false;
    @ViewChild('mainBody', { read: ElementRef }) mainBody: ElementRef;
    @ViewChild('cardContainer') cardContainer: ElementRef;

    mediaType: MediaTypeEnum;
    tweetType: TwitterSubTypeEnum;
    linkedinType: LinkedinSubTypeEnum;
    blueskyType: BlueskySubTypeEnum;

    overlayContainer: Element;
    preferences: PreferencesModel;
    expandAnimation: AnimationFactory;
    collapseAnimation: AnimationFactory;

    impressionLabel: string = 'Followers';
    embedVideoUrl: string = '';
    sentimentName: string;
    iframeTitle: string;
    bodyTextAlign: string = 'left';
    dateFormat: string;

    isTVEyes: boolean = false;
    enableExpandToggle: boolean = true;
    canExpand: boolean = true;
    expanded: boolean = false;
    contentVisible: boolean = false;

    protected subscriptions: Subscription[] = [];

    readonly socialNetworkTypeColorMap = socialNetworkTypeColorMap;
    readonly mediaTypes = MediaTypeEnum;
    readonly tweetTypeEnum = TwitterSubTypeEnum;
    readonly linkedinTypeEnum = LinkedinSubTypeEnum;
    readonly blueskyTypeEnum = BlueskySubTypeEnum;

    readonly twitterAvatarSizes = TwitterAvatarSize;
    readonly imageDefault = socialThumbnailDefaultImage;
    readonly buttonTypes = ButtonTypesEnum;
    readonly maxWordCount: number = 15;

    constructor(
        protected currentUserStateService: CurrentUserStateService,
        protected coreDialogService: CoreDialogService,
        protected injector: Injector,
        private localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngOnInit() {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                prefs => {
                    this.preferences = prefs;
                    this.dateFormat = prefs.getDateFormatString(true, true, true);
                }
            )
        );

        if (this.mention?.isTvEyes) {
            this.isTVEyes = this.mention?.isTvEyes();
        }

        this.bodyTextAlign = getTextAlign(this.mention?.body);

        this.overlayContainer = document.getElementById('main-content');

        this.mediaType = this.mention.mediaType?.id;
        if (this.mediaType === MediaTypeEnum.YOUTUBE || this.mediaType === MediaTypeEnum.REDDIT) {
            this.impressionLabel = 'Subscribers';
        }

        if (this.mediaType === MediaTypeEnum.YOUTUBE) {
            this.embedVideoUrl = webVideoHelper.getYoutubeEmbedURL(this.mention.url);
            this.iframeTitle = this.localizationService.transform(
                'Social.postViewer.iframeTitle',
                { media: this.localizationService.transform(`MediaType.${this.mediaType}`)}
            );
        }

        this.setMentionSentiment();

        this.tweetType = this.mention?.tweetData?.type;
        this.linkedinType = this.mention?.linkedinData?.type;
        this.blueskyType = this.mention?.blueskyData?.type;
    }

    setMentionSentiment() {
        if (this.mention.sentimentName === 'Sentiment.POSITIVE') {
            this.sentimentName = 'Positive';
        } else if (this.mention.sentimentName === 'Sentiment.NEGATIVE') {
            this.sentimentName = 'Negative';
        } else if (this.mention.sentimentName === 'Sentiment.NEUTRAL') {
            this.sentimentName = 'Neutral';
        } else {
            this.sentimentName = 'Not Available';
        }
    }

    openPostViewer(): void {
        if (!this.mention.translating) {
            const animationTrigger = new BehaviorSubject(
                new FullProfileAnimationModel(
                    FullProfileDialogAnimationsEnum.slideInOutAnimation,
                    OverlayAnimationStateEnum.open
                )
            );

            const ioModel =  {
                inputs: {
                    mention: this.mention,
                    animationTrigger: animationTrigger,
                    overlayContainer: this.overlayContainer
                },
                outputs: {
                    dialogClose: (closeAction) => {
                        // do nothing right now
                    }
                }
            };

            this.coreDialogService.open(PostViewerDialogComponent, ioModel, this.injector);
        }
    }
}
